import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '@/layouts/Sidebar';
import Header from '@/layouts/Header';

const ORNX_THEME_KEY = 'ornx-theme';

const readThemeCookie = (): 'dark' | 'light' | null => {
    const match = document.cookie.match(new RegExp(`(?:^|;)\\s*${ORNX_THEME_KEY}=([^;]*)`));
    if (!match) return null;
    return match[1] === 'dark' ? 'dark' : 'light';
};

const readThemeStorage = (): 'dark' | 'light' | null => {
    const value = window.localStorage.getItem(ORNX_THEME_KEY);
    if (!value) return null;
    return value === 'dark' ? 'dark' : 'light';
};

const MainLayout: React.FC = () => {
    const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
        const cookieTheme = readThemeCookie();
        if (cookieTheme) return cookieTheme === 'dark';

        const storageTheme = readThemeStorage();
        if (storageTheme) return storageTheme === 'dark';

        // Fallback to system preference
        return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
    });

    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
    const location = useLocation();
    const navigate = useNavigate();
    const currentPath = location.pathname;

    // Update HTML class for Tailwind dark mode and persist theme selector
    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }

        const themeValue = isDarkMode ? 'dark' : 'light';
        window.localStorage.setItem(ORNX_THEME_KEY, themeValue);

        // Save theme in cookie for shared state across *.orionex.id
        const domain = window.location.hostname.includes('orionex.id') ? 'domain=.orionex.id;' : '';
        document.cookie = `${ORNX_THEME_KEY}=${themeValue}; ${domain} path=/; max-age=31536000`; // 1 year expiry
    }, [isDarkMode]);

    // Close mobile menu when navigating
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [currentPath]);

    const toggleTheme = () => setIsDarkMode(!isDarkMode);
    const toggleSidebar = () => setIsSidebarCollapsed(!isSidebarCollapsed);
    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

    return (
        <div className="flex min-h-screen bg-slate-50 dark:bg-orionDark relative overflow-hidden font-sans">
            {/* Ambient Glows */}
            <div className="fixed top-[-10%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-orionBlue/10 blur-[100px] mix-blend-multiply dark:mix-blend-screen animate-float pointer-events-none z-0"></div>
            <div className="fixed bottom-[-10%] right-[-10%] w-[40vw] h-[40vw] rounded-full bg-orionYellow/10 blur-[100px] mix-blend-multiply dark:mix-blend-screen animate-float pointer-events-none z-0" style={{ animationDelay: '2s' }}></div>

            {/* Mobile Backdrop */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-slate-900/50 backdrop-blur-md z-40 md:hidden"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            {/* Sidebar Navigation */}
            <Sidebar
                isDarkMode={isDarkMode}
                isCollapsed={isSidebarCollapsed}
                isMobileMenuOpen={isMobileMenuOpen}
                toggleSidebar={toggleSidebar}
                closeMobileMenu={() => setIsMobileMenuOpen(false)}
                onNavigate={(path) => navigate(path)}
                currentPath={currentPath}
            />

            {/* Main Content Area */}
            <div
                className={`flex-1 flex flex-col transition-all duration-300 ease-in-out h-screen overflow-hidden ${isSidebarCollapsed ? 'md:pl-20' : 'md:pl-72'} w-full`}
            >
                <Header
                    isDarkMode={isDarkMode}
                    toggleTheme={toggleTheme}
                    toggleMobileMenu={toggleMobileMenu}
                    currentPath={currentPath}
                />

                <main className="flex-1 overflow-x-hidden overflow-y-auto w-full relative z-10">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default MainLayout;
